import type { BoundedText, Section, WorkloadStructure, WorkloadStrategyValue } from "@k8s-dashboard/contracts";
import { Panel } from "@/components/primitives";
import { useT, type Translate } from "@/i18n/I18nProvider";
import { since } from "@/lib/format";

function textValue(text: BoundedText, t: Translate): string {
  if (text.state === "absent") return t("workload.absent");
  if (text.state === "withheld_too_large") return t("workload.withheld");
  return `${text.value ?? ""}${text.truncated ? ` ${t("workload.truncated")}` : ""}`;
}

function strategyValue(value: WorkloadStrategyValue | undefined, t: Translate): string {
  if (!value || value.state === "unavailable") return t("workload.unavailable");
  return value.value ?? t("workload.unavailable");
}

export function WorkloadStructureCard({ section, referenceIso }: { section?: Section<WorkloadStructure>; referenceIso?: string }) {
  const { t } = useT("workload");
  if (!section) return null;
  if (section.status === "degraded" && !section.data) {
    return (
      <Panel title={t("workload.title")} section={section} referenceIso={referenceIso}>
        <div className="state state--degraded" role="note" data-state={section.reason}>
          <span className="state__glyph" aria-hidden="true">▲</span>
          <span className="state__title">{t("workload.unsupported")}</span>
        </div>
      </Panel>
    );
  }
  if (!section.data) return null;
  const data = section.data;
  return (
    <Panel title={t("workload.title")} section={section} referenceIso={referenceIso}>
      <div className="grid">
        {data.strategy && (
          <section>
            <h3>{t("workload.strategy")}</h3>
            <dl className="resource-drawer__facts">
              <div><dt>{t("workload.type")}</dt><dd>{data.strategy.type}</dd></div>
              {data.strategy.maxSurge && <div><dt>{t("workload.maxSurge")}</dt><dd>{strategyValue(data.strategy.maxSurge, t)}</dd></div>}
              {data.strategy.maxUnavailable && <div><dt>{t("workload.maxUnavailable")}</dt><dd>{strategyValue(data.strategy.maxUnavailable, t)}</dd></div>}
            </dl>
          </section>
        )}
        <section>
          <h3>{t("workload.conditions")}</h3>
          {data.conditions.state === "unsupported" ? (
            <p className="muted" role="note">{t("workload.conditions.unsupported")}</p>
          ) : data.conditions.items.length === 0 ? (
            <p className="muted">{t("workload.conditions.empty")}</p>
          ) : (
            <table className="ds-data-table ds-data-table--compact">
              <caption className="visually-hidden">{t("workload.conditions")}</caption>
              <thead><tr><th scope="col">{t("workload.type")}</th><th scope="col">{t("workload.status")}</th><th scope="col">{t("workload.reason")}</th><th scope="col">{t("workload.message")}</th></tr></thead>
              <tbody>{data.conditions.items.map((condition) => (
                <tr key={condition.type}><td>{condition.type}</td><td>{condition.status}</td><td>{textValue(condition.reason, t)}</td><td>{textValue(condition.message, t)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {data.conditions.omitted ? <p className="muted">{t("workload.conditions.omitted", { count: data.conditions.omitted })}</p> : null}
        </section>
        {data.replicaSets && (
          <section>
            <h3>{t("workload.rs")}</h3>
            {data.replicaSets.scanTruncated ? (
              <p className="muted" role="note">{t("workload.rs.scan")}</p>
            ) : (
              <>
                <p className="muted">{t("workload.rs.shown", { shown: data.replicaSets.shown, total: data.replicaSets.total })}</p>
                <table className="ds-data-table ds-data-table--compact">
                  <caption className="visually-hidden">{t("workload.rs")}</caption>
                  <thead><tr><th scope="col">{t("workload.rs.name")}</th><th scope="col">{t("workload.rs.revision")}</th><th scope="col">{t("workload.rs.current")}</th><th scope="col">{t("workload.rs.pods")}</th><th scope="col">{t("workload.rs.created")}</th></tr></thead>
                  <tbody>{data.replicaSets.rows.map((row) => (
                    <tr key={row.uid}>
                      <td className="ds-ident">{row.name}</td><td>{row.revisionState === "valid" ? row.revision : t("workload.unknown")}</td>
                      <td>{row.current ? t("workload.yes") : t("workload.no")}</td><td>{row.pods}</td>
                      <td>{row.createdAt && referenceIso ? since(row.createdAt, referenceIso) : row.createdAt || t("workload.unknown")}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </>
            )}
            <p className="muted" role="note">{t("workload.rs.metadata")}</p>
          </section>
        )}
        {data.cronJob && (
          <section>
            <h3>{t("workload.cron")}</h3>
            <dl className="resource-drawer__facts">
              <div><dt>{t("workload.cron.schedule")}</dt><dd>{textValue(data.cronJob.schedule, t)}</dd></div>
              <div><dt>{t("workload.cron.suspend")}</dt><dd>{data.cronJob.suspended ? t("workload.yes") : t("workload.no")}</dd></div>
              <div><dt>{t("workload.cron.last")}</dt><dd>{data.cronJob.lastScheduleTime ?? t("workload.absent")}</dd></div>
              <div><dt>{t("workload.cron.success")}</dt><dd>{data.cronJob.lastSuccessfulTime ?? t("workload.absent")}</dd></div>
            </dl>
            <p className="muted" role="note">{t("workload.cron.history")}</p>
          </section>
        )}
      </div>
    </Panel>
  );
}
